"use client";

import { IBaseLabelValue } from "@/interfaces";
import { useRouter } from "next/navigation";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import { cn } from "@/lib";
import InputDataContent from "../inputDataContent";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import React, { useRef, useState } from "react";
import { Accordion } from "@/components";
import { IRawPurchasedMilitaryCardInquiryResData } from "@/interfaces/inquiries/governmental";

const MilitaryCardInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedMilitaryCardInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const cardData = data.data.data;
  const { national_code } = data.data.input;
  const [openAccordionId, setOpenAccordionId] = useState<number | null>(0);
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("militaryCard.inputLabel"),
      value: national_code,
    },
  ];

  const resultContent: IBaseLabelValue[] = [
    {
      label: t("militaryCard.cardDate"),
      value: "cardDate",
    },
    {
      label: t("militaryCard.cardIssueDate"),
      value: "cardIssueDate",
    },
    {
      label: t("militaryCard.releaseDate"),
      value: "releaseDate",
    },
    {
      label: t("militaryCard.postBarcode"),
      value: "postBarcode",
    },
    {
      label: t("militaryCard.cardNo"),
      value: "cardNo",
    },
    {
      label: t("militaryCard.dispatchDate"),
      value: "dispatchDate",
    },
  ];

  interface IDataItem {
    [key: string]: string;
  }

  function mapDataToResultContent(
    data: IDataItem[],
    resultContent: IBaseLabelValue[],
  ): IBaseLabelValue[][] {
    return data.map((item) => {
      const filtered = resultContent
        .map((field) => {
          const fieldValue = item[field.value];
          return fieldValue ? { label: field.label, value: fieldValue } : null;
        })
        .filter((field): field is IBaseLabelValue => field !== null); // ✅ type predicate

      return filtered;
    });
  }

  const result = mapDataToResultContent(cardData, resultContent);

  function buildTableRows(
    data: Record<string, any>[],
    resultContent: IBaseLabelValue[],
  ): string[][] {
    const tableRows: string[][] = [];

    data.forEach((item, index) => {
      // ✅ عنوان بخش
      tableRows.push([`ایتم شماره ${index + 1}`, ""]);

      // ✅ فیلدها
      resultContent.forEach((field) => {
        const fieldValue = item[field.value];
        if (fieldValue) {
          tableRows.push([field.label, fieldValue]);
        }
      });

      // ✅ جداکننده بین آبجکت‌ها
      if (index < data.length - 1) {
        tableRows.push(["", ""]);
      }
    });

    return tableRows;
  }

  const tableRows = buildTableRows(cardData, resultContent);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  return (
    <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("militaryCard.title")}
        </h2>

        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("militaryServiceStatus.resultTitle")}
          </h2>
          <DownloadPdf
            id="military-card-inquiry-download-pdf"
            fileName={`گزارش وضعیت کارت پایان خدمت ${national_code}`}
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
        </FlexBetween>
        <FlexCenter className={cn("mb-5 block w-full md:flex md:flex-col")}>
          {cardData.map((item) => (
            <>
              {item.errorCode && (
                <div className="shadow-main mb-10 flex w-full flex-col rounded-xl p-8 text-center">
                  {item.errorMessage}
                </div>
              )}
            </>
          ))}
          {result.map((item, i) => (
            <div key={i} className="w-full">
              <Accordion
                title={`کارت ${i + 1}`}
                open={i === openAccordionId}
                className="mb-2 w-full"
                onOpenChange={(isOpen) => {
                  setOpenAccordionId(isOpen ? i : null);
                }}
              >
                <InputDataContent className={cn("w-full")} content={item} />
              </Accordion>
            </div>
          ))}
        </FlexCenter>
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="military-card-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`گزارش وضعیت کارت پایان خدمت ${national_code}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-military-card-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-military-card-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Military_Card_Status.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>

        {/* This div will be used for PDF generation - DetailCreditCard style */}
        <div
          ref={pdfContentRef}
          style={{
            position: "fixed",
            left: "-9999px",
            top: "-9999px",
            width: "800px",
            fontFamily: "IRANYekan, sans-serif",
            margin: "2rem auto",
          }}
        >
          <PdfTemplate
            tableTitle={`گزارش وضعیت کارت پایان خدمت ${national_code}`}
            resultContent={tableRows}
            code={data.code}
            dateTime={data.created_at}
          />
        </div>
      </FlexCol>
    </div>
  );
};

export default MilitaryCardInquiryDetails;
